import { Button, IconButton, LuIcon, Text, toast, useAlertDialog } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, type User } from "@overmap-ai/common"
import { APIError, selectActiveOrganizationAccess, selectOrganizationAccessUserMapping } from "@overmap-ai/core"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useCallback, useMemo } from "react"

import { modelIconMapping } from "../../../mappings/icons"
import { organizationAccessLevelData } from "../../../mappings/organizations"
import { sdk } from "../../../sdk"
import { useAppSelector } from "../../../store"
import { formatTimestampDate } from "../../../utils/date"
import { OrganizationAccessLevelSelect } from "../../DropdownSelects/OrganizationAccessLevelSelect"
import { UserIcon } from "../../Icons/UserIcon"
import { OrganizationAccessRequired } from "../../OrganizationAccessRequired/AccessRequired"
import { ColumnSortButton, ColumnSortIcon } from "../components"

export const useOrganizationUserTableColumns = () => {
	const organizationAccesses = useAppSelector(selectOrganizationAccessUserMapping)

	return useMemo<ColumnDef<User>[]>(
		() => [
			{
				id: "userAvatar",
				enableHiding: false,
				cell({ row }) {
					return <UserIcon userId={row.original?.id} />
				},
			},
			{
				id: "email",
				accessorKey: "email",
				enableHiding: false,
				meta: {
					plainHeader: "Email",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Email
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: ({ getValue }) => {
					const email = getValue<string>()
					return <Text accentColor="base">{email}</Text>
				},
			},
			{
				id: "role",
				accessorFn: (row) => organizationAccesses[row.id]!.access_level,
				enableHiding: false,
				meta: {
					plainHeader: "Role",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Role
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ row }) {
					const user = row.original
					const activeOrganizationAccess = useAppSelector(selectActiveOrganizationAccess)
					const organizationAccessLevel = organizationAccesses[user.id]!.access_level

					const handleOrganizationAccessLevelChange = useCallback(
						(organizationAccessLevel: OrganizationAccessLevel) => {
							void sdk.organizationAccesses.update({
								...organizationAccesses[user.id]!,
								access_level: organizationAccessLevel,
							})
						},
						[user.id],
					)

					return (
						<OrganizationAccessLevelSelect
							required
							value={organizationAccessLevel}
							onValueChange={handleOrganizationAccessLevelChange}
							disabled={activeOrganizationAccess?.access_level !== OrganizationAccessLevel.ADMIN}
							size="sm"
						>
							<Button variant="ghost" accentColor="base" size="sm">
								{organizationAccessLevelData[organizationAccessLevel].label}
							</Button>
						</OrganizationAccessLevelSelect>
					)
				},
			},
			{
				id: "created_at",
				enableHiding: false,
				accessorFn: (row) => organizationAccesses[row.id].created_at,
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Joined
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: ({ getValue }) => {
					return (
						<Text accentColor="base" className="whitespace-nowrap">
							{formatTimestampDate(getValue<string>())}
						</Text>
					)
				},
			},
			{
				id: "removeUser",
				enableHiding: false,
				cell: function Cell({ row }) {
					const user = row.original
					const organizationAccesses = useAppSelector(selectOrganizationAccessUserMapping)
					const openAlertDialog = useAlertDialog()

					const handleRemoveClick = useCallback(() => {
						openAlertDialog({
							title: "Remove user",
							description:
								"This will remove the user from the organization. You can invite them back at any time.",
							size: "sm",
							onAction: () => {
								sdk.organizationAccesses
									.delete(organizationAccesses[user.id].uuid)
									.catch((error: unknown) => {
										toast.error({
											title: "Unable to remove user",
											description:
												error instanceof APIError
													? error.message
													: "An unexpected error occurred",
										})
									})
							},
							action: "Remove",
						})
					}, [openAlertDialog, organizationAccesses, user.id])

					return (
						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="disable">
							<IconButton
								aria-label="Remove user"
								onClick={handleRemoveClick}
								variant="ghost"
								accentColor="base"
							>
								<LuIcon icon={modelIconMapping.close} />
							</IconButton>
						</OrganizationAccessRequired>
					)
				},
			},
		],
		[organizationAccesses],
	)
}

export const useOrganizationUserTable = (args: Omit<TableOptions<User>, "getCoreRowModel">) => {
	return useReactTable<User>({
		...args,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
}
